import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar as CalendarIcon, MapPin, Clock, Sparkles, Tag } from "lucide-react";
import { format, isBefore, startOfDay } from "date-fns";
import { nl } from "date-fns/locale";
import { fetchPerformances } from "@/lib/api";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { track } from "@/lib/tracker";

export default function Agenda() {
  const [performances, setPerformances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    track("visit");
    fetchPerformances()
      .then((data) => setPerformances(data))
      .catch(() => setPerformances([]))
      .finally(() => setLoading(false));
  }, []);

  const { upcoming, past } = useMemo(() => {
    const today = startOfDay(new Date());
    const sorted = [...performances].sort((a, b) => a.date.localeCompare(b.date));
    return {
      upcoming: sorted.filter((p) => !isBefore(new Date(p.date), today)),
      past: sorted.filter((p) => isBefore(new Date(p.date), today)).reverse(),
    };
  }, [performances]);

  return (
    <div className="min-h-screen bg-night text-[#F8F5FF] font-body overflow-x-hidden" data-testid="agenda-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong py-4">
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10">
          <Link to="/" className="flex items-center gap-2 group" data-testid="agenda-nav-logo">
            <Sparkles className="w-6 h-6 text-gold group-hover:rotate-12 transition-transform duration-300" />
            <span className="font-display text-xl sm:text-2xl font-bold tracking-tight">
              Magic <span className="gold-text">Seppe</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#B5A9C9] hover:text-gold transition-colors"
            data-testid="agenda-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Terug naar website
          </Link>
        </nav>
      </header>

      <main className="pt-32 pb-20 px-6 lg:px-10">
        {/* Hero */}
        <section className="relative max-w-5xl mx-auto text-center mb-20" data-testid="agenda-hero">
          <div className="absolute inset-0 star-field opacity-30 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8">
              <CalendarIcon className="w-4 h-4 text-gold" />
              <span className="text-xs uppercase tracking-widest text-[#B5A9C9]">Optredens & agenda</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05]">
              Waar Seppe<br />
              jou kan <span className="gold-text italic">verrassen</span>.
            </h1>
            <p className="mt-8 text-lg text-[#B5A9C9] max-w-2xl mx-auto">
              Ontdek waar Magic Seppe binnenkort optreedt. Kom langs, kijk mee
              en laat je betoveren — of boek hem voor je eigen event.
            </p>
          </div>
        </section>

        {/* Upcoming */}
        <section className="max-w-5xl mx-auto mb-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">— Komende optredens</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">
                {upcoming.length} {upcoming.length === 1 ? "show" : "shows"} op de planning
              </h2>
            </div>
          </div>

          {loading ? (
            <div className="glass rounded-2xl p-10 text-center">
              <p className="text-[#B5A9C9]">Optredens laden...</p>
            </div>
          ) : upcoming.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center">
              <p className="text-[#B5A9C9]">
                Momenteel geen publieke shows ingepland. Boek Seppe gerust voor jouw eigen event!
              </p>
            </div>
          ) : (
            <ul className="space-y-4" data-testid="agenda-upcoming-list">
              {upcoming.map((p, i) => (
                <PerformanceRow key={p.id || i} p={p} index={i} />
              ))}
            </ul>
          )}
        </section>

        {/* Past */}
        {past.length > 0 && (
          <section className="max-w-5xl mx-auto mb-20" data-testid="agenda-past-section">
            <div className="text-xs uppercase tracking-[0.3em] text-[#7A6C96] mb-3">— Voorbije optredens</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#B5A9C9] mb-6">Archief</h2>
            <ul className="space-y-3 opacity-70">
              {past.map((p, i) => (
                <PerformanceRow key={p.id || i} p={p} index={i} dimmed />
              ))}
            </ul>
          </section>
        )}

        {/* CTA */}
        <section className="max-w-3xl mx-auto text-center mt-24" data-testid="agenda-cta">
          <div className="magical-divider mb-12" />
          <h3 className="font-display text-3xl sm:text-4xl font-bold mb-4">
            Wil je Seppe op <span className="gold-text italic">jouw</span> event?
          </h3>
          <p className="text-[#B5A9C9] mb-8">
            Stuur een aanvraag via het formulier — Seppe neemt binnen 24 uur contact op.
          </p>
          <Link
            to="/#boeken"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gold text-night-deep font-semibold hover:bg-gold-light hover:-translate-y-1 transition-all duration-300 gold-glow-hover"
            data-testid="agenda-cta-book"
          >
            Boek Magic Seppe
            <Sparkles className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}

function PerformanceRow({ p, index, dimmed }) {
  const dateObj = new Date(p.date);
  const day = format(dateObj, "d", { locale: nl });
  const month = format(dateObj, "MMM", { locale: nl }).replace(".", "");
  const weekday = format(dateObj, "EEEE", { locale: nl });
  const fullDate = format(dateObj, "d MMMM yyyy", { locale: nl });

  return (
    <li
      className={`group relative overflow-hidden rounded-2xl glass hover:border-gold/50 transition-all duration-500 ${
        dimmed ? "" : "hover:-translate-y-1"
      }`}
      data-testid={`agenda-row-${index}`}
    >
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-gold to-mystic opacity-50 group-hover:opacity-100 transition-opacity" />
      <div className="grid grid-cols-12 gap-4 items-center p-5 sm:p-6">
        {/* Date block */}
        <div className="col-span-3 sm:col-span-2 flex flex-col items-center justify-center text-center bg-night-surface/50 rounded-xl py-3 group-hover:bg-night-surface transition-colors">
          <div className="font-display text-3xl sm:text-4xl font-bold gold-text leading-none">
            {day}
          </div>
          <div className="text-xs uppercase tracking-widest text-[#B5A9C9] mt-1">
            {month}
          </div>
        </div>

        {/* Details */}
        <div className="col-span-9 sm:col-span-7">
          <div className="text-xs uppercase tracking-widest text-gold mb-1">
            {weekday} · {fullDate}
          </div>
          <div className="font-display text-lg sm:text-xl font-semibold mb-2 group-hover:text-gold transition-colors">
            {p.venue}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#B5A9C9]">
            {p.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gold/70" />
                {p.location}
              </span>
            )}
            {p.time && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-gold/70" />
                {p.time}
              </span>
            )}
            {p.type && (
              <span className="inline-flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-gold/70" />
                {p.type}
              </span>
            )}
          </div>
        </div>

        {/* Status / icon */}
        <div className="hidden sm:flex col-span-3 justify-end">
          {!dimmed ? (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border border-gold/40 text-gold bg-gold/5">
              <Sparkles className="w-3 h-3" />
              Gepland
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border border-[#7A6C96]/30 text-[#7A6C96]">
              Voorbij
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
